import React, { useMemo, useState } from "react"
import {
    Alert,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    MenuItem,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material"
import AddIcon from "@mui/icons-material/Add"
import EditIcon from "@mui/icons-material/Edit"
import DeleteIcon from "@mui/icons-material/Delete"
import { useTranslation } from "react-i18next"

export type PricingGridRow = {
    id: string
    customerTypeId?: string | null
    customerTypeLabel?: string
    unitPriceMinor: number
    effectiveFrom?: string | null
    effectiveTo?: string | null
    status?: "active" | "inactive" | "expired"
}

export type PricingGridDraft = {
    customer_type_id?: string | null
    unit_price_minor: number
    effective_from?: string | null
    effective_to?: string | null
}

type CustomerTypeOption = {
    id: string
    label: string
}

type PricingGridTableProps = {
    title?: string
    rows?: PricingGridRow[]
    customerTypes?: CustomerTypeOption[]
    loading?: boolean
    disabled?: boolean
    onCreate?: (payload: PricingGridDraft) => Promise<void> | void
    onUpdate?: (
        rowId: string,
        payload: PricingGridDraft
    ) => Promise<void> | void
    onDelete?: (rowId: string) => Promise<void> | void
}

const ALL_CUSTOMER_TYPES_VALUE = "__ALL_CUSTOMER_TYPES__"

function formatMinor(value: number) {
    return `${(Number(value || 0) / 100).toFixed(2)} €`
}

function toInputDate(value?: string | null) {
    if (!value) return ""
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return ""
    return date.toISOString().slice(0, 10)
}

function formatDate(value: string | null | undefined, locale: string) {
    if (!value) return "—"
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return value
    return date.toLocaleDateString(locale || "fr-FR")
}

function getStatus(row: PricingGridRow): "active" | "inactive" | "expired" {
    if (row.status) return row.status

    const now = new Date().getTime()
    const from = row.effectiveFrom
        ? new Date(row.effectiveFrom).getTime()
        : null
    const to = row.effectiveTo ? new Date(row.effectiveTo).getTime() : null

    if (from !== null && Number.isFinite(from) && from > now) {
        return "inactive"
    }

    if (to !== null && Number.isFinite(to) && to < now) {
        return "expired"
    }

    return "active"
}

function PricingGridTable({
    title,
    rows = [],
    customerTypes = [],
    loading = false,
    disabled = false,
    onCreate,
    onUpdate,
    onDelete,
}: PricingGridTableProps) {
    const { t, i18n } = useTranslation()
    const activeLocale = i18n.language || "fr-FR"
    const [dialogOpen, setDialogOpen] = useState(false)
    const [editingRow, setEditingRow] = useState<PricingGridRow | null>(null)
    const [customerTypeId, setCustomerTypeId] = useState(
        ALL_CUSTOMER_TYPES_VALUE
    )
    const [unitPrice, setUnitPrice] = useState("")
    const [effectiveFrom, setEffectiveFrom] = useState("")
    const [effectiveTo, setEffectiveTo] = useState("")
    const [submitting, setSubmitting] = useState(false)
    const [formError, setFormError] = useState("")

    const statusLabels = useMemo(
        () => ({
            active: t("pricingGrid.status.active"),
            inactive: t("pricingGrid.status.inactive"),
            expired: t("pricingGrid.status.expired"),
        }),
        [t]
    )

    const tableTitle = title || t("pricingGrid.title")

    const openCreateDialog = () => {
        setEditingRow(null)
        setCustomerTypeId(ALL_CUSTOMER_TYPES_VALUE)
        setUnitPrice("")
        setEffectiveFrom("")
        setEffectiveTo("")
        setFormError("")
        setDialogOpen(true)
    }

    const openEditDialog = (row: PricingGridRow) => {
        setEditingRow(row)
        setCustomerTypeId(row.customerTypeId || ALL_CUSTOMER_TYPES_VALUE)
        setUnitPrice((Number(row.unitPriceMinor || 0) / 100).toFixed(2))
        setEffectiveFrom(toInputDate(row.effectiveFrom))
        setEffectiveTo(toInputDate(row.effectiveTo))
        setFormError("")
        setDialogOpen(true)
    }

    const closeDialog = () => {
        if (submitting) return
        setDialogOpen(false)
    }

    const handleSubmit = async () => {
        const normalizedPrice = Number(unitPrice.replace(",", "."))

        if (!Number.isFinite(normalizedPrice) || normalizedPrice < 0) {
            setFormError(String(t("pricingGrid.error.invalidPrice")))
            return
        }

        if (
            effectiveFrom &&
            effectiveTo &&
            new Date(effectiveFrom).getTime() > new Date(effectiveTo).getTime()
        ) {
            setFormError(String(t("pricingGrid.error.invalidDateRange")))
            return
        }

        const payload: PricingGridDraft = {
            customer_type_id:
                customerTypeId === ALL_CUSTOMER_TYPES_VALUE || !customerTypeId
                    ? null
                    : customerTypeId,
            unit_price_minor: Math.round(normalizedPrice * 100),
            effective_from: effectiveFrom || null,
            effective_to: effectiveTo || null,
        }

        setSubmitting(true)
        setFormError("")
        try {
            if (editingRow) {
                await onUpdate?.(editingRow.id, payload)
            } else {
                await onCreate?.(payload)
            }
            setDialogOpen(false)
        } catch (error: any) {
            setFormError(
                error?.message || String(t("pricingGrid.error.saveFailed"))
            )
        } finally {
            setSubmitting(false)
        }
    }

    const handleDelete = async (rowId: string) => {
        if (!onDelete || disabled || loading) return
        const confirmed = window.confirm(String(t("pricingGrid.confirmDelete")))
        if (!confirmed) return

        try {
            await onDelete(rowId)
        } catch (error) {
            console.error("Failed to delete pricing rule:", error)
        }
    }

    return (
        <Paper sx={{ p: 2 }}>
            <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "stretch", sm: "center" }}
                spacing={1.5}
                sx={{ mb: 1.5 }}
            >
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {tableTitle}
                </Typography>
                <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={openCreateDialog}
                    disabled={disabled || loading}
                >
                    {t("pricingGrid.addRule")}
                </Button>
            </Stack>

            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>
                            {t("pricingGrid.columns.customerType")}
                        </TableCell>
                        <TableCell>
                            {t("pricingGrid.columns.unitPrice")}
                        </TableCell>
                        <TableCell>
                            {t("pricingGrid.columns.startDate")}
                        </TableCell>
                        <TableCell>
                            {t("pricingGrid.columns.endDate")}
                        </TableCell>
                        <TableCell>{t("pricingGrid.columns.status")}</TableCell>
                        <TableCell align="right">
                            {t("pricingGrid.columns.actions")}
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {loading ? (
                        <TableRow>
                            <TableCell colSpan={6}>
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                >
                                    <CircularProgress size={16} />
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        {t("pricingGrid.loading")}
                                    </Typography>
                                </Stack>
                            </TableCell>
                        </TableRow>
                    ) : rows.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={6}>
                                {t("pricingGrid.empty")}
                            </TableCell>
                        </TableRow>
                    ) : (
                        rows.map((row) => (
                            <TableRow key={row.id}>
                                <TableCell>
                                    {row.customerTypeLabel ??
                                        t("pricingGrid.allCustomerTypes")}
                                </TableCell>
                                <TableCell>
                                    {formatMinor(row.unitPriceMinor)}
                                </TableCell>
                                <TableCell>
                                    {formatDate(
                                        row.effectiveFrom,
                                        activeLocale
                                    )}
                                </TableCell>
                                <TableCell>
                                    {formatDate(row.effectiveTo, activeLocale)}
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        size="small"
                                        color={
                                            getStatus(row) === "active"
                                                ? "success"
                                                : getStatus(row) === "expired"
                                                ? "warning"
                                                : "default"
                                        }
                                        label={statusLabels[getStatus(row)]}
                                        variant={
                                            getStatus(row) === "inactive"
                                                ? "outlined"
                                                : "filled"
                                        }
                                    />
                                </TableCell>
                                <TableCell align="right">
                                    <IconButton
                                        size="small"
                                        onClick={() => openEditDialog(row)}
                                        disabled={disabled}
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => handleDelete(row.id)}
                                        disabled={disabled}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>

            <Dialog
                open={dialogOpen}
                onClose={closeDialog}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>
                    {editingRow
                        ? t("pricingGrid.dialog.editTitle")
                        : t("pricingGrid.dialog.createTitle")}
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        {formError ? (
                            <Alert severity="error">{formError}</Alert>
                        ) : null}

                        <TextField
                            label={String(
                                t("pricingGrid.fields.customerType")
                            )}
                            select
                            value={customerTypeId}
                            onChange={(e) =>
                                setCustomerTypeId(e.target.value)
                            }
                            SelectProps={{
                                displayEmpty: true,
                                renderValue: (selected) => {
                                    const selectedValue = String(
                                        selected || ""
                                    )
                                    if (
                                        selectedValue ===
                                            ALL_CUSTOMER_TYPES_VALUE ||
                                        !selectedValue
                                    ) {
                                        return String(
                                            t("pricingGrid.allCustomerTypes")
                                        )
                                    }
                                    return (
                                        customerTypes.find(
                                            (ct) => ct.id === selectedValue
                                        )?.label || selectedValue
                                    )
                                },
                            }}
                            fullWidth
                        >
                            <MenuItem value={ALL_CUSTOMER_TYPES_VALUE}>
                                {t("pricingGrid.allCustomerTypes")}
                            </MenuItem>
                            {customerTypes.map((ct) => (
                                <MenuItem key={ct.id} value={ct.id}>
                                    {ct.label}
                                </MenuItem>
                            ))}
                        </TextField>

                        <TextField
                            label={String(t("pricingGrid.fields.unitPrice"))}
                            value={unitPrice}
                            onChange={(e) => setUnitPrice(e.target.value)}
                            fullWidth
                            type="number"
                            inputProps={{ min: "0", step: "0.01" }}
                        />

                        <TextField
                            label={String(t("pricingGrid.fields.startDate"))}
                            value={effectiveFrom}
                            onChange={(e) => setEffectiveFrom(e.target.value)}
                            fullWidth
                            type="date"
                            InputLabelProps={{ shrink: true }}
                        />

                        <TextField
                            label={String(t("pricingGrid.fields.endDate"))}
                            value={effectiveTo}
                            onChange={(e) => setEffectiveTo(e.target.value)}
                            fullWidth
                            type="date"
                            InputLabelProps={{ shrink: true }}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDialog} disabled={submitting}>
                        {t("common.cancel")}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={submitting}
                    >
                        {submitting ? t("common.saving") : t("common.save")}
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    )
}

export default PricingGridTable
